import React, { FC, ReactNode, useMemo } from 'react';

import { CONTEXT_NAMES } from '@adsk/alloy-helpers';
import { createGlobalStore } from '@adsk/alloy-react-context';

import { CurrentAccount } from './CurrentAccountEntitlementsContext';
import {
  EntitlementsProject,
  EntitlementsUser,
} from './projectEntitlementTypes';
import { Account } from './stores/AccountEntitlementsStore';
import {
  Company,
  Role,
  Subscription,
  SubscriptionConstruction,
  User,
} from './types';

export type MockAlloyProviderProps = {
  children: ReactNode;
  loading?: boolean;
  error?: Error | null;
  user?: Partial<User> | null;
  users?: Partial<User>[];
  deletedUsers?: Partial<User>[];
  currentUser?: Partial<EntitlementsUser>;
  companies?: Partial<Company>[];
  roles?: Partial<Role>[];
  currentProject?: Partial<EntitlementsProject>;
  currentAccount?: Partial<CurrentAccount>;
  projectId?: string;
  productId?: string;
  accountId?: string;
  toolId?: string;
  accounts?: Partial<Account>[];
  subscriptions?: { results: Partial<Subscription>[] } | null;
  subscriptionsConstruction?: {
    results: Partial<SubscriptionConstruction>[];
  } | null;
  customHelpLink?: string;
};

// eslint-disable-next-line @typescript-eslint/no-explicit-any
type Setter = (partial: any) => void;

const noopAsync = () => Promise.resolve();

const buildCommon = (loading: boolean, error: Error | null | undefined) => ({
  loading,
  error,
  _started: true,
  fetchPage: noopAsync,
  fetchData: noopAsync,
});

/**
 * Memoizes a global Zustand store creation. The store is recreated only when
 * `deps` change, preventing the unconditional overwrite that would otherwise
 * happen on every render of the consuming provider.
 */
const useGlobalStoreMemo = <T,>(
  contextName: string,
  storeCreator: (set: Setter) => T,
  deps: React.DependencyList,
) => {
  useMemo(
    () => createGlobalStore(storeCreator, contextName),
    // contextName is stable, storeCreator captures `deps` by closure
    // eslint-disable-next-line react-hooks/exhaustive-deps
    deps,
  );
};

export const MockAlloyProvider: FC<MockAlloyProviderProps> = ({
  children,
  user,
  users,
  deletedUsers,
  currentUser,
  currentProject,
  currentAccount,
  companies,
  roles,
  projectId,
  productId,
  accountId,
  toolId,
  loading = false,
  error,
  accounts,
  subscriptions,
  subscriptionsConstruction,
  customHelpLink,
}) => {
  const common = buildCommon(loading, error);

  useGlobalStoreMemo(
    CONTEXT_NAMES.CurrentProjectIdContext,
    (set) => ({
      projectId,
      setProjectId: (newProjectId?: string) => set({ projectId: newProjectId }),
    }),
    [projectId],
  );

  useGlobalStoreMemo(
    CONTEXT_NAMES.CurrentProductIdContext,
    (set) => ({
      productId,
      setProductId: (newProductId?: string) => set({ productId: newProductId }),
    }),
    [productId],
  );

  useGlobalStoreMemo(
    CONTEXT_NAMES.CurrentAccountIdContext,
    (set) => ({
      accountId,
      setAccountId: (newAccountId?: string) => set({ accountId: newAccountId }),
    }),
    [accountId],
  );

  useGlobalStoreMemo(
    CONTEXT_NAMES.CurrentToolIdContext,
    (set) => ({
      toolId,
      setToolId: (newToolId?: string) => set({ toolId: newToolId }),
    }),
    [toolId],
  );

  useGlobalStoreMemo(
    CONTEXT_NAMES.AccountEntitlementsContext,
    () => ({ data: { accounts }, ...common }),
    [accounts, loading, error],
  );

  useGlobalStoreMemo(
    CONTEXT_NAMES.SubscriptionsContext,
    () => ({ data: subscriptions, ...common }),
    [subscriptions, loading, error],
  );

  useGlobalStoreMemo(
    CONTEXT_NAMES.SubscriptionsConstructionContext,
    () => ({ data: subscriptionsConstruction, ...common }),
    [subscriptionsConstruction, loading, error],
  );

  useGlobalStoreMemo(
    CONTEXT_NAMES.ProjectEntitlementsContext,
    () => ({ user: currentUser, currentProject, ...common }),
    [currentUser, currentProject, loading, error],
  );

  useGlobalStoreMemo(
    CONTEXT_NAMES.CurrentAccountEntitlementsContext,
    () => ({ currentAccount, ...common }),
    [currentAccount, loading, error],
  );

  useGlobalStoreMemo(
    CONTEXT_NAMES.UserContext,
    () => ({ data: user, ...common }),
    [user, loading, error],
  );

  useGlobalStoreMemo(
    CONTEXT_NAMES.UsersContext,
    () => ({ entities: users, ...common }),
    [users, loading, error],
  );

  useGlobalStoreMemo(
    CONTEXT_NAMES.DeletedUsersContext,
    () => ({ entities: deletedUsers, ...common }),
    [deletedUsers, loading, error],
  );

  useGlobalStoreMemo(
    CONTEXT_NAMES.CompaniesContext,
    () => ({ entities: companies, ...common }),
    [companies, loading, error],
  );

  useGlobalStoreMemo(
    CONTEXT_NAMES.RolesContext,
    () => ({ entities: roles, ...common }),
    [roles, loading, error],
  );

  useGlobalStoreMemo(
    CONTEXT_NAMES.CustomHelpContext,
    () => ({ link: customHelpLink, setLink: () => {} }),
    [customHelpLink],
  );

  return <>{children}</>;
};
